import {
    useEffect,
    Fragment,
    useState,
    useLayoutEffect
} from "react";
import {
    Image,
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Button,
    Text,
    NCoreUIKitToast
} from "ncore-ui-kit";
import {
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation
} from "@react-navigation/native";
import {
    useClearByFocusCell,
    useBlurOnFulfill,
    CodeField,
    Cursor
} from "react-native-confirmation-code-field";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import {
    type CheckForgotPWCodeRequestType
} from "../../../services/restAPI/actions/checkForgotPWCode/types";
import {
    type SetForgottenPasswordRequestType
} from "../../../services/restAPI/actions/setForgottenPassword/types";
import {
    type StartForgetPasswordRequestType
} from "../../../services/restAPI/actions/startForgetPassword/types";
import type RootStackParamList from "../../../types/navigation";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    LockKeyhole,
    Mail
} from "lucide-react-native";
import md5 from "md5";
import GMKonyaLogo from "../../../assets/images/gmkonyalogo.png";
import GMKonyaLogoDark from "../../../assets/images/gmkonyalogodark.png";
import {
    RESTService
} from "../../../services/restAPI";

type Step = "EMAIL" | "CODE" | "NEW_PASSWORD";

const ResetPassword = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        activeTheme,
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        windowWidth
    } = useDebouncedDimensions();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [canResend,
        setCanResend] = useState(false);
    const [isTimeUp,
        setIsTimeUp] = useState(false);
    const [step,
        setStep] = useState<Step>("EMAIL");
    const [loading,
        setLoading] = useState(false);
    const [password,
        setPassword] = useState("");
    const [token,
        setToken] = useState("");
    const [email,
        setEmail] = useState("");
    const [time,
        setTime] = useState(180);
    const [code,
        setCode] = useState("");

    const CELL_COUNT = 6;

    const ref = useBlurOnFulfill({
        value: code,
        cellCount: CELL_COUNT
    });
    const [props,
        getCellOnLayoutHandler] = useClearByFocusCell({
        value: code,
        setValue: setCode
    });

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        backgroundColor: colors.content.container.default,
                        paddingHorizontal: spaces.spacingMd,
                        paddingTop: inset.top
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        if (step === "NEW_PASSWORD") setStep("CODE");
                        else if (step === "CODE") setStep("EMAIL");
                        else navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("forgot-password")}
                    </Text>
                </View>
            </View>
        });
    }, [
        navigation,
        step
    ]);

    useEffect(() => {
        let timer: ReturnType<typeof setInterval>;
        if (step === "CODE" && time > 0) {
            timer = setInterval(() => {
                setTime((prev) => {
                    const nextTime = prev - 1;
                    if (nextTime <= 120) {
                        setCanResend(true);
                    }
                    if (nextTime <= 0) {
                        setIsTimeUp(true);
                        clearInterval(timer);
                    }
                    return nextTime;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [
        step,
        time
    ]);

    const isValidEmail = (text: string) => {
        if(!text) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(text);
    };

    const handleSendCode = async () => {
        if(!email || !isValidEmail(email)){
            NCoreUIKitToast.open({
                title: localize("error"),
                subTitle: localize("please-enter-valid-email"),
                type: "danger"
            });
            return;
        }
        setLoading(true);
        const params: StartForgetPasswordRequestType = {
            mail: email.trim()
        };
        RESTService.action("StartForgetPassword", params)
            .then((res) => {
                console.log(res);
                setToken(res.payload.token);
                setTime(res.payload.remainingTime || 240);
                setIsTimeUp(false);
                setCanResend(false);
                setTimeout(() => {
                    setLoading(false);
                    setStep("CODE");
                }, 1000);
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const handleVerifyCode = async (currentCode: string) => {
        if(!currentCode || !email || !token){
            NCoreUIKitToast.open({
                title: localize("error"),
                subTitle: localize("please-enter-verification-code"),
                type: "danger"
            });
            return;
        }
        setLoading(true);
        const params: CheckForgotPWCodeRequestType = {
            mail: email.trim(),
            code: currentCode,
            token: token
        };
        RESTService.action("CheckForgotPWCode", params)
            .then((res) => {
                console.log(res);
                setToken(res.payload.token);
                setTimeout(() => {
                    setLoading(false);
                    setStep("NEW_PASSWORD");
                }, 1000);
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const handleResetPassword = async () => {
        if(!password){
            NCoreUIKitToast.open({
                title: localize("error"),
                subTitle: localize("please-enter-new-password"),
                type: "danger"
            });
            return;
        }
        if(!code || !email || !token){
            NCoreUIKitToast.open({
                title: localize("error"),
                subTitle: localize("session-expired"),
                type: "danger"
            });
            return;
        }
        setLoading(true);
        const params: SetForgottenPasswordRequestType = {
            mail: email.trim(),
            token: token,
            newPassword: md5(password)
        };
        RESTService.action("SetForgottenPassword", params)
            .then((res) => {
                console.log(res);
                setTimeout(() => {
                    setLoading(false);
                    navigation.navigate("Auth", {
                        screen: "Login"
                    } as never);
                }, 1000);
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const renderMailStep = () => {
        if (step !== "EMAIL") return null;
        return <Fragment>
            <TextInput
                spreadBehaviour="stretch"
                placeholder={localize("email")}
                icon={() => <Mail
                    color={colors.content.icon.default}
                    size={15}
                />}
                type={email.length > 0 && !isValidEmail(email) ? "danger" : "default"}
                hintText={email.length > 0 && !isValidEmail(email) ? (localize("invalid-email")) : undefined}
                style={{
                    marginVertical: spaces.spacingLg
                }}
                value={email}
                onChangeText={setEmail}
                keyboardType={"email-address"}
            />
            <Button
                spreadBehaviour="stretch"
                title={localize("continue")}
                size="large"
                isLoading={loading}
                isDisabled={!isValidEmail(email)}
                onPress={handleSendCode}
                style={{
                    marginTop: spaces.spacingLg
                }}
            />
        </Fragment>;
    };

    const renderCodeStep = () => {
        if (step !== "CODE") return null;

        return <Fragment>
            <CodeField
                ref={ref}
                {...props}
                value={code}
                onChangeText={(e) => {
                    setCode(e);
                    if (e.length === CELL_COUNT) {
                        handleVerifyCode(e);
                    }
                }}
                cellCount={CELL_COUNT}
                rootStyle={[
                    stylesheet.codeFieldRoot,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
                keyboardType="number-pad"
                textContentType="oneTimeCode"
                editable={!isTimeUp}
                renderCell={({
                    index,
                    symbol,
                    isFocused
                }) => <View
                    key={index}
                    style={[
                        {
                            ...stylesheet.cell,
                            height: (windowWidth * 15) / 100,
                            width: (windowWidth * 11) / 100,
                            marginLeft: index === 0 ? 0 : spaces.spacingLg / 2,
                            backgroundColor: colors.content.container.mid,
                            marginBottom: spaces.spacingXl,
                            borderRadius: radiuses.sm
                        },
                        isFocused && {
                            borderColor: colors.content.border.emphasized,
                            borderWidth: borders.line
                        }
                    ]}
                    onLayout={getCellOnLayoutHandler(index)}
                >
                    <Text
                        variant="headlineLargeSize"
                        color="high"
                    >
                        {symbol || (isFocused ? <Cursor /> : null)}
                    </Text>
                </View>
                }
            />
            <Text
                variant="bodyLargeSize"
                color="low"
                style={{
                    marginBottom: spaces.spacingXl
                }}
            >
                {localize("sms-otp-code-instruction")}
            </Text>
            <Button
                spreadBehaviour="stretch"
                title={localize("sms-otp-resend-code")}
                onPress={handleSendCode}
                isDisabled={!canResend}
                style={{
                    marginTop: spaces.spacingSm
                }}
            />
        </Fragment>;
    };

    const renderNewPasswordStep = () => {
        if (step !== "NEW_PASSWORD") return null;
        return <Fragment>
            <TextInput
                spreadBehaviour="stretch"
                variant="hidden"
                placeholder= {localize("enter-password")}
                icon={() => <LockKeyhole
                    size={20}
                />}
                type={password.length > 0 && password.length < 6 ? "danger" : "default"}
                hintText={password.length > 0 && password.length < 6 ? (localize("password-too-short")) : undefined}
                style={{
                    marginVertical: spaces.spacingLg
                }}
                onChangeText={(text) => {
                    setPassword(text);
                }}
            />
            <Text
                variant="bodyLargeSize"
                color="low"
                style={{
                    marginTop: spaces.spacingSm
                }}
            >
                {localize("password-requirements")}
            </Text>
            <Button
                spreadBehaviour="stretch"
                title={localize("confirm")}
                isLoading={loading}
                size="large"
                isDisabled={!password || password.length < 6}
                onPress={handleResetPassword}
                customColor="black"
                customBorderColor="black"
                style={{
                    marginTop: spaces.spacingXl
                }}
            />
        </Fragment>;
    };

    return <PageContainer
        style={[
            stylesheet.container,
            {
                padding: spaces.spacingXl
            }
        ]}
    >
        <Image
            resizeMode="contain"
            source={activeTheme === "dark" ? GMKonyaLogoDark : GMKonyaLogo}
            width={175}
            style={[
                stylesheet.logo,
                {
                    marginTop: spaces.spacingXl * 2,
                    marginBottom: spaces.spacingLg
                }
            ]}
        />
        {renderMailStep()}
        {renderCodeStep()}
        {renderNewPasswordStep()}
    </PageContainer>;
};
export default ResetPassword;
